"use client";

import { useEffect, useState } from "react";
import { Package, Save, Trash2, Plus, Wrench } from "lucide-react";
import PageHeader from "@/components/layout/PageHeader";
import { notify, confirmDialog } from "@/components/ui/dialogs";
import { get, post, put, del, can, getCurrentOrganization } from "@/utils/service";
import { apiMessage, fieldErrors, money, label } from "@/utils/api";
import FloatingDrawer, { DrawerHeader, darkInput, darkLabel, darkPrimary, darkSecondary, darkDanger } from "@/components/crm/FloatingDrawer";
import { useCounts, countText } from "@/components/crm/useCounts";
import ListShell, { toolbarInput, pill } from "@/components/crm/ListShell";
import { useRemoteList } from "@/components/crm/useRemoteList";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";

interface Product {
  id: string;
  type: "product" | "service";
  sku: string | null;
  name: string;
  description: string | null;
  category_id: string | null;
  category?: { id: string; name: string } | null;
  unit: string | null;
  price: string;
  currency: string;
  cost: string | null;
  tax_rate_id: string | null;
  tax_rate?: { id: string; name: string; rate: string } | null;
  is_active: boolean;
}

interface Option { id: string; name: string; rate?: string }

export default function ProductsPage() {
  const editable = can("products.update");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [type, setType] = useState("");
  const [showInactive, setShowInactive] = useState(false);
  const [sort, setSort] = useState("name");
  const list = useRemoteList<Product>("/products", { search: query, type, include_inactive: showInactive ? "1" : undefined, sort }, 20);
  const { counts, loading: countsLoading } = useCounts("/products", { active: {}, everything: { include_inactive: "1" }, products: { type: "product" }, services: { type: "service" } }, list.rows.length);
  const [categories, setCategories] = useState<Option[]>([]);
  const [taxRates, setTaxRates] = useState<Option[]>([]);
  const [editing, setEditing] = useState<Product | null>(null);
  const [open, setOpen] = useState(false);
  const base = getCurrentOrganization()?.base_currency || "UGX";
  const blank = { type: "service", sku: "", name: "", description: "", category_id: "", unit: "unit", price: "", currency: base, cost: "", tax_rate_id: "", is_active: true };
  const [form, setForm] = useState(blank);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [newCategory, setNewCategory] = useState("");

  useEffect(() => { const t = setTimeout(() => setQuery(search.trim()), 300); return () => clearTimeout(t); }, [search]);

  const loadOptions = () => {
    get<{ data: { categories: Option[] } }>("/product-categories").then((r) => setCategories(r.data.categories)).catch(() => undefined);
    get<{ data: { tax_rates: Option[] } }>("/tax-rates").then((r) => setTaxRates(r.data.tax_rates)).catch(() => undefined);
  };
  useEffect(loadOptions, []);

  const openForm = (p: Product | null) => {
    setEditing(p);
    setErrors({});
    setForm(p ? {
      type: p.type, sku: p.sku ?? "", name: p.name, description: p.description ?? "", category_id: p.category_id ?? "", unit: p.unit ?? "unit",
      price: p.price, currency: p.currency, cost: p.cost ?? "", tax_rate_id: p.tax_rate_id ?? "", is_active: p.is_active,
    } : blank);
    setOpen(true);
  };
  const set = (k: keyof typeof blank, v: string | boolean) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    setErrors({});
    const body = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v === "" ? null : v]));
    try {
      if (editing) await put(`/products/${editing.id}`, body);
      else await post("/products", body);
      notify(editing ? "Product updated" : "Product added");
      setOpen(false);
      list.reload();
    } catch (e) {
      setErrors(fieldErrors(e));
      notify(apiMessage(e, "Could not save the product."));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing || !await confirmDialog(`Remove ${editing.name}? If it's already on a quote or invoice it is deactivated instead, so those documents stay intact.`)) return;
    try {
      const r = await del<{ message: string }>(`/products/${editing.id}`);
      notify(r.message);
      setOpen(false);
      list.reload();
    } catch (e) {
      notify(apiMessage(e, "Could not remove the product."));
    }
  };

  const addCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      const r = await post<{ data: { category: Option } }>("/product-categories", { name: newCategory.trim() });
      setNewCategory("");
      loadOptions();
      set("category_id", r.data.category.id);
    } catch (e) {
      notify(apiMessage(e, "Could not add the category."));
    }
  };

  const card = (p: Product) => (
    <div className={`flex h-full flex-col ${p.is_active ? "" : "opacity-70"}`}>
      <div className="flex items-start gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${p.type === "service" ? "bg-blue-50 text-blue-600" : "bg-primary/10 text-primary"}`}>
          {p.type === "service" ? <Wrench className="h-5 w-5" /> : <Package className="h-5 w-5" />}
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-gray-900">{p.name}</p>
          <p className="truncate text-xs text-gray-500">{[label(p.type), p.sku, p.category?.name].filter(Boolean).join(" · ")}</p>
        </div>
        {!p.is_active && <span className={pill("bg-gray-100 text-gray-600")}>Inactive</span>}
      </div>
      {p.description && <p className="mt-3 text-xs text-gray-600 line-clamp-2">{p.description}</p>}
      <div className="min-h-[1rem] flex-1" />
        <div className="flex items-end justify-between gap-2 border-t border-gray-100 pt-3">
        <p className="text-lg font-semibold text-gray-900">{money(p.price, p.currency)}{p.unit && <span className="text-xs font-normal text-gray-500"> / {p.unit}</span>}</p>
        <span className="text-xs text-gray-500">{p.tax_rate ? `${p.tax_rate.name} ${Number(p.tax_rate.rate)}%` : "No tax"}</span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Products & Services" description="Your catalogue, used on deals, quotes and invoices" />

        <ListShell
          renderCard={card}
          rowKey={(r) => r.id}
          rows={list.rows}
          loading={list.loading}
          pagination={list.pagination}
          onPage={list.setPage}
          noun="items"
          onRowClick={editable ? openForm : undefined}
          grid={{
            id: "products",
            search: <SearchInput className="w-full sm:w-64" placeholder="Search name or SKU…" value={search} onChange={(e) => setSearch(e.target.value)} />,
            filters: (<>
              <label className="text-xs text-gray-600">Type
                <AppSelect className={`${toolbarInput} mt-1`} value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="">Products and services</option><option value="product">Products</option><option value="service">Services</option>
                </AppSelect>
              </label>
              <label className="flex items-center gap-2 text-xs text-gray-600"><input type="checkbox" className="accent-primary" checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} />Show inactive items</label>
            </>),
            activeFilters: (type ? 1 : 0) + (showInactive ? 1 : 0),
            onClearFilters: () => { setType(""); setShowInactive(false); },
            sortOptions: [{ value: "name", label: "Name" }, { value: "-price", label: "Highest price" }, { value: "price", label: "Lowest price" }, { value: "sku", label: "SKU" }, { value: "-created_at", label: "Newest" }],
            sort, onSort: setSort,
            stats: [
              { label: "Active items", value: countText(counts.active) },
              { label: "Products", value: countText(counts.products) },
              { label: "Services", value: countText(counts.services) },
              { label: "Inactive", value: counts.everything != null && counts.active != null ? countText(counts.everything - counts.active) : "—", hint: "Hidden from new quotes" },
            ],
            statsLoading: countsLoading,
          }}
          onAdd={can("products.create") ? () => openForm(null) : undefined}
          addLabel="Add Item"
          onPerPage={list.setPerPage}
          empty={{ icon: Package, title: "Your catalogue is empty", text: "Add the products and services you sell to price quotes and invoices quickly.", action: "Add First Item" }}
        />
      </div>

      <FloatingDrawer isOpen={open} onClose={() => setOpen(false)}>
        <DrawerHeader title={editing ? "Edit Item" : "Add Item"} subtitle="Prices are copied onto documents, so changes here never alter issued quotes or invoices" />
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className={darkLabel}>Type</label><AppSelect className={darkInput} value={form.type} onChange={(e) => set("type", e.target.value)}><option value="service">Service</option><option value="product">Product</option></AppSelect></div>
            <div><label className={darkLabel}>SKU / Code</label><input className={darkInput} value={form.sku} onChange={(e) => set("sku", e.target.value)} />{errors.sku && <p className="mt-1 text-xs text-red-400">{errors.sku}</p>}</div>
          </div>
          <div><label className={darkLabel}>Name *</label><input className={darkInput} value={form.name} onChange={(e) => set("name", e.target.value)} />{errors.name && <p className="mt-1 text-xs text-red-400">{errors.name}</p>}</div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-1"><label className={darkLabel}>Price *</label><input type="number" min="0" step="0.01" className={darkInput} value={form.price} onChange={(e) => set("price", e.target.value)} /></div>
            <div><label className={darkLabel}>Currency</label><AppSelect className={darkInput} value={form.currency} onChange={(e) => set("currency", e.target.value)}>{[...new Set([form.currency, base, "UGX", "USD", "EUR", "GBP", "KES"])].map((c) => <option key={c}>{c}</option>)}</AppSelect></div>
            <div><label className={darkLabel}>Per</label><input className={darkInput} value={form.unit} onChange={(e) => set("unit", e.target.value)} placeholder="unit, hour, month" /></div>
          </div>
          {errors.price && <p className="-mt-2 text-xs text-red-400">{errors.price}</p>}
          <div className="grid grid-cols-2 gap-4">
            <div><label className={darkLabel}>Tax</label><AppSelect className={darkInput} value={form.tax_rate_id} onChange={(e) => set("tax_rate_id", e.target.value)}><option value="">No tax</option>{taxRates.map((t) => <option key={t.id} value={t.id}>{t.name} ({Number(t.rate)}%)</option>)}</AppSelect></div>
            <div><label className={darkLabel}>Cost (internal)</label><input type="number" min="0" step="0.01" className={darkInput} value={form.cost} onChange={(e) => set("cost", e.target.value)} /></div>
          </div>
          <div>
            <label className={darkLabel}>Category</label>
            <AppSelect className={darkInput} value={form.category_id} onChange={(e) => set("category_id", e.target.value)}><option value="">None</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</AppSelect>
            <div className="mt-2 flex gap-2">
              <input className={darkInput} placeholder="New category" value={newCategory} onChange={(e) => setNewCategory(e.target.value)} />
              <button type="button" onClick={addCategory} disabled={!newCategory.trim()} className="rounded-lg bg-gray-800 px-3 text-xs text-white disabled:opacity-50"><Plus className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div><label className={darkLabel}>Description</label><textarea rows={3} className={`${darkInput} resize-none`} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
          <label className="flex items-center gap-2 text-xs text-white"><input type="checkbox" className="h-4 w-4 accent-primary" checked={form.is_active} onChange={(e) => set("is_active", e.target.checked)} />Available for new quotes and invoices</label>
        </div>
        <div className="p-6 border-t border-gray-800 space-y-3">
          <button onClick={save} disabled={busy || !form.name || form.price === ""} className={darkPrimary}><Save className="w-4 h-4" />{busy ? "Saving..." : editing ? "Update Item" : "Add Item"}</button>
          {editing && can("products.delete") && <button onClick={remove} className={darkDanger}><Trash2 className="w-4 h-4" />Remove</button>}
          <button onClick={() => setOpen(false)} className={darkSecondary}>Cancel</button>
        </div>
      </FloatingDrawer>
    </div>
  );
}
